/** A touch organ keyboard. */

import { noteLabel, pitchClassOf } from "../core/pitch.js";

/** Pitch classes of the white keys, in order within an octave. */
const WHITE_CLASSES = [0, 2, 4, 5, 7, 9, 11];
/**
 * Black keys, as the pitch class and the index of the white key they sit after.
 * C# follows the first white key of the octave, Eb the second, and so on.
 */
const BLACK_CLASSES: readonly { pitchClass: number; afterWhite: number }[] = [
  { pitchClass: 1, afterWhite: 0 },
  { pitchClass: 3, afterWhite: 1 },
  { pitchClass: 6, afterWhite: 3 },
  { pitchClass: 8, afterWhite: 4 },
  { pitchClass: 10, afterWhite: 5 },
];

export interface KeyboardHandlers {
  onPress(midi: number): void;
  onRelease(midi: number): void;
}

export class Keyboard {
  private readonly keys = new Map<number, HTMLElement>();
  /** Which note each active pointer is holding, so multi-touch releases cleanly. */
  private readonly held = new Map<number, number>();

  constructor(
    private readonly container: HTMLElement,
    private readonly handlers: KeyboardHandlers,
  ) {
    // A stray pointerup outside any key would otherwise leave a note sounding.
    window.addEventListener("pointerup", (event) => this.releasePointer(event.pointerId));
    window.addEventListener("pointercancel", (event) => this.releasePointer(event.pointerId));
  }

  /** Rebuild the keyboard for `octaves` octaves starting at C of `lowestOctave`. */
  render(lowestOctave: number, octaves: number): void {
    this.releaseAllPointers();
    this.container.replaceChildren();
    this.keys.clear();

    const whites = document.createElement("div");
    whites.className = "keys-white";

    const blacks = document.createElement("div");
    blacks.className = "keys-black";

    const whiteCount = octaves * WHITE_CLASSES.length;
    const whiteWidth = 100 / whiteCount;

    for (let octave = 0; octave < octaves; octave++) {
      const baseMidi = (lowestOctave + octave + 1) * 12;

      WHITE_CLASSES.forEach((pitchClass, index) => {
        const midi = baseMidi + pitchClass;
        const key = this.createKey(midi, "white");
        // Label each C so you can find your place without counting.
        if (pitchClass === 0) {
          const tag = document.createElement("span");
          tag.className = "key-tag";
          tag.textContent = noteLabel(midi);
          key.append(tag);
        }
        void index;
        whites.append(key);
      });

      for (const { pitchClass, afterWhite } of BLACK_CLASSES) {
        const midi = baseMidi + pitchClass;
        const key = this.createKey(midi, "black");
        const whiteIndex = octave * WHITE_CLASSES.length + afterWhite;
        key.style.left = `calc(${(whiteIndex + 1) * whiteWidth}% - var(--black-key-width) / 2)`;
        blacks.append(key);
      }
    }

    this.container.append(whites, blacks);
  }

  private createKey(midi: number, kind: "white" | "black"): HTMLElement {
    const key = document.createElement("button");
    key.type = "button";
    key.className = `key key-${kind}`;
    key.dataset.midi = String(midi);
    key.setAttribute("aria-label", noteLabel(midi));
    if (pitchClassOf(midi) === 0) key.dataset.isC = "true";

    key.addEventListener("pointerdown", (event) => {
      event.preventDefault();
      // Capture so the release is reported here even if the finger slides off,
      // which is the difference between a stuck note and a clean one.
      key.setPointerCapture(event.pointerId);
      this.held.set(event.pointerId, midi);
      this.handlers.onPress(midi);
    });
    key.addEventListener("pointerup", (event) => {
      event.preventDefault();
      this.releasePointer(event.pointerId);
    });
    key.addEventListener("contextmenu", (event) => event.preventDefault());

    this.keys.set(midi, key);
    return key;
  }

  private releasePointer(pointerId: number): void {
    const midi = this.held.get(pointerId);
    if (midi === undefined) return;
    this.held.delete(pointerId);
    this.handlers.onRelease(midi);
  }

  private releaseAllPointers(): void {
    for (const pointerId of [...this.held.keys()]) this.releasePointer(pointerId);
  }

  /** Highlight the notes currently sounding. */
  setSounding(midis: Iterable<number>): void {
    const active = new Set(midis);
    for (const [midi, key] of this.keys) {
      key.classList.toggle("is-sounding", active.has(midi));
    }
  }

  get lowestMidi(): number | null {
    const midis = [...this.keys.keys()];
    return midis.length ? Math.min(...midis) : null;
  }

  get highestMidi(): number | null {
    const midis = [...this.keys.keys()];
    return midis.length ? Math.max(...midis) : null;
  }
}
